// Zustand store mirroring server state for the connected player.
// PLAN.md §7.1 — single store, plain Zustand, no middleware.

import { create } from 'zustand'
import type {
  Cell,
  CellNonSpectator,
  FeedStatus,
  MissedEvent,
  PlayerSnapshot,
  PositionStatus,
  PriceHistoryPoint,
  RosterDelta,
  ServerMessage,
  TickEvent,
  TombstoneSnapshot,
  WelcomeMessage,
} from '@trading-grounds/shared'

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'replaced'
  | 'auth_failed'
  | 'closed'

/** Per-player live state mirrored from server `pnl`/`liquidate`/`move_confirmed` events. */
export type ClientPlayerLive = {
  cell: Cell
  /** Stake on current position (server-authoritative). 0 if in spectator. */
  stake: number
  /** stake_t − $5 since the game's first entry. 0 if in spectator. */
  cumulative_pnl: number
  status: PositionStatus | 'spectator'
  /** Optimistic ghost: if set, render this player at this cell with reduced opacity. */
  optimistic_cell: Cell | null
  /** Last tick this player was confirmed-active (for tombstone reconciliation). */
  last_tick_id: number
}

export type Toast = {
  id: number
  message: string
  expiresAt: number
}

type Selfish = {
  // Identity
  player_id: string
  handle: string
  display_handle: string
  avatar_seed: string
  bankroll: number
  starting_capital: number
  active_stake: number
  active_cell: Cell
  active_status: PositionStatus | 'spectator'
  /** PLAN.md §7.8 — bumped on bank/liquidate to trigger MoneyPill pulse. */
  portfolioVersion: number
  active_position_entry_price: number | null
  active_position_carried_stake: number | null
  /** True between local move click and server move_confirmed. */
  optimistic_busy: boolean
  optimistic_target: Cell | null
}

type Store = {
  // Connection + tick
  status: ConnectionStatus
  tick_id: number
  price: number | null
  feed_status: FeedStatus

  // Identity
  self: Selfish | null

  // Roster
  players: Map<string, PlayerSnapshot & ClientPlayerLive>
  tombstones: TombstoneSnapshot[]

  // Chart history
  priceRing: PriceHistoryPoint[]

  // Timeframe (display-only, doesn't affect game state)
  timeframe: '15m' | '1h' | '4h'

  // Toasts (used for missed_events digest)
  toasts: Toast[]

  /** Wall-clock ms when self was last liquidated; gates the LIQUIDATED splash. */
  selfLiquidatedAt: number | null

  // ── actions ────────────────────────────────────────────────────────────
  setStatus: (s: ConnectionStatus) => void
  applyServerMessage: (msg: ServerMessage) => void
  beginOptimisticMove: (target: Cell) => void
  clearOptimisticMove: () => void
  setTimeframe: (tf: '15m' | '1h' | '4h') => void
  pushToast: (message: string, ttlMs?: number) => void
  dismissToast: (id: number) => void
  reset: () => void
}

const TOAST_TTL_DEFAULT_MS = 6_000
const PRICE_RING_CAP = 14_400

let toastSeq = 0

function applyWelcome(_state: Store, msg: WelcomeMessage): Partial<Store> {
  const players = new Map<string, PlayerSnapshot & ClientPlayerLive>()
  for (const p of msg.roster.players) {
    players.set(p.id, {
      ...p,
      cell: p.cell,
      stake: 0,
      cumulative_pnl: 0,
      status: p.cell === 'spectator' ? 'spectator' : 'active',
      optimistic_cell: null,
      last_tick_id: msg.tick_id,
    })
  }
  // Make sure self is in the roster.
  if (!players.has(msg.player_id)) {
    players.set(msg.player_id, {
      id: msg.player_id,
      handle: msg.handle,
      avatar_seed: msg.avatar_seed,
      cell: 'spectator',
      bankroll: msg.bankroll,
      starting_capital: msg.starting_capital,
      stake: 0,
      cumulative_pnl: 0,
      status: 'spectator',
      optimistic_cell: null,
      last_tick_id: msg.tick_id,
    })
  }

  const self: Selfish = {
    player_id: msg.player_id,
    handle: msg.handle,
    display_handle: msg.display_handle,
    avatar_seed: msg.avatar_seed,
    bankroll: msg.bankroll,
    starting_capital: msg.starting_capital,
    active_stake: 0,
    active_cell: 'spectator',
    active_status: 'spectator',
    portfolioVersion: 0,
    active_position_entry_price: null,
    active_position_carried_stake: null,
    optimistic_busy: false,
    optimistic_target: null,
  }

  return {
    self,
    players,
    tombstones: msg.roster.tombstones,
    tick_id: msg.tick_id,
    price: msg.current_price,
    feed_status: msg.feed_status,
    priceRing: msg.price_history.slice(),
  }
}

function applyTickMessage(state: Store, events: TickEvent[], tick_id: number, price: number, feed_status: FeedStatus): Partial<Store> {
  const players = new Map(state.players)
  let self = state.self
  let portfolioVersion = self?.portfolioVersion ?? 0
  let selfLiquidatedAt = state.selfLiquidatedAt

  for (const e of events) {
    const p = players.get(e.player_id)

    if (e.type === 'pnl') {
      if (p) {
        players.set(e.player_id, {
          ...p,
          stake: e.stake,
          cumulative_pnl: e.cumulative_pnl,
          status: e.status,
          last_tick_id: tick_id,
        })
      }
      if (self && e.player_id === self.player_id) {
        self = {
          ...self,
          active_stake: e.stake,
          active_status: e.status,
        }
      }
    } else if (e.type === 'liquidate') {
      if (p) {
        players.set(e.player_id, {
          ...p,
          stake: 0,
          status: 'liquidated',
          last_tick_id: tick_id,
        })
      }
      if (self && e.player_id === self.player_id) {
        self = {
          ...self,
          active_stake: 0,
          active_status: 'liquidated',
          active_cell: e.cell,
          optimistic_busy: false,
          optimistic_target: null,
        }
        portfolioVersion += 1
        selfLiquidatedAt = Date.now()
      }
    } else if (e.type === 'move_confirmed') {
      if (p) {
        players.set(e.player_id, {
          ...p,
          cell: e.cell,
          stake: e.carried_stake,
          status: e.cell === 'spectator' ? 'spectator' : 'active',
          optimistic_cell: null,
          last_tick_id: tick_id,
        })
      }
      if (self && e.player_id === self.player_id) {
        self = {
          ...self,
          active_cell: e.cell,
          active_position_entry_price: e.cell === 'spectator' ? null : e.entry_price,
          active_position_carried_stake: e.cell === 'spectator' ? null : e.carried_stake,
          active_stake: e.cell === 'spectator' ? 0 : e.carried_stake,
          active_status: e.cell === 'spectator' ? 'spectator' : 'active',
          optimistic_busy: false,
          optimistic_target: null,
        }
      }
    } else if (e.type === 'bank_confirmed') {
      // Update the player's bankroll in the roster (used by the leaderboard).
      if (p) {
        players.set(e.player_id, { ...p, bankroll: e.new_bankroll, stake: 0 })
      }
      if (self && e.player_id === self.player_id) {
        self = {
          ...self,
          bankroll: e.new_bankroll,
          active_stake: 0,
          active_cell: 'spectator',
          active_status: 'spectator',
          active_position_entry_price: null,
          active_position_carried_stake: null,
          optimistic_busy: false,
          optimistic_target: null,
        }
        portfolioVersion += 1
      }
    } else if (e.type === 'tombstone_expired') {
      // handled via roster_delta tombstones_removed
    }
  }

  // Update price ring
  let priceRing = state.priceRing
  if (price > 0) {
    priceRing = priceRing.concat({ t: Date.now(), price })
    if (priceRing.length > PRICE_RING_CAP) priceRing = priceRing.slice(-PRICE_RING_CAP)
  }

  return {
    players,
    self: self ? { ...self, portfolioVersion } : self,
    tick_id,
    price,
    feed_status,
    priceRing,
    selfLiquidatedAt,
  }
}

function applyRosterDelta(state: Store, msg: RosterDelta): Partial<Store> {
  const players = new Map(state.players)
  if (msg.added) {
    for (const a of msg.added) {
      const existing = players.get(a.id)
      players.set(a.id, {
        ...a,
        stake: existing?.stake ?? 0,
        cumulative_pnl: existing?.cumulative_pnl ?? 0,
        status: existing?.status ?? (a.cell === 'spectator' ? 'spectator' : 'active'),
        optimistic_cell: null,
        last_tick_id: msg.tick_id,
      })
    }
  }
  if (msg.removed) {
    for (const id of msg.removed) {
      players.delete(id)
    }
  }
  if (msg.moved) {
    for (const m of msg.moved) {
      const existing = players.get(m.player_id)
      if (existing) {
        players.set(m.player_id, {
          ...existing,
          cell: m.cell,
          status: m.cell === 'spectator' ? 'spectator' : existing.status,
          optimistic_cell: null,
        })
      }
    }
  }

  let tombstones = state.tombstones
  if (msg.tombstones_added) {
    tombstones = tombstones.concat(msg.tombstones_added)
  }
  if (msg.tombstones_removed) {
    const removed = msg.tombstones_removed
    tombstones = tombstones.filter(
      (t) =>
        !removed.some(
          (r: { player_id: string; cell: CellNonSpectator; liquidated_tick: number }) =>
            r.player_id === t.player_id &&
            r.cell === t.cell &&
            r.liquidated_tick === t.liquidated_tick,
        ),
    )
  }

  return { players, tombstones }
}

function missedEventToToast(e: MissedEvent): string {
  if (e.type === 'liquidated') {
    return `Liquidated at $${e.liquidated_price.toFixed(2)} on ${e.cell.replace('_', ' ')} (peak $${e.peak_stake.toFixed(2)})`
  }
  return `Auto-banked $${e.amount.toFixed(2)} while you were away`
}

export const useStore = create<Store>((set) => ({
  status: 'idle',
  tick_id: 0,
  price: null,
  feed_status: 'delayed_60s',
  self: null,
  players: new Map(),
  tombstones: [],
  priceRing: [],
  timeframe: '15m',
  toasts: [],
  selfLiquidatedAt: null,

  setStatus: (s) => set({ status: s }),

  applyServerMessage: (msg) => {
    if (msg.type === 'welcome') {
      set((state) => applyWelcome(state, msg))
      return
    }
    if (msg.type === 'tick') {
      set((state) => applyTickMessage(state, msg.events, msg.tick_id, msg.price, msg.feed_status))
      return
    }
    if (msg.type === 'roster_delta') {
      set((state) => applyRosterDelta(state, msg))
      return
    }
    if (msg.type === 'missed_events') {
      const ts: Toast[] = msg.events.map((e) => ({
        id: ++toastSeq,
        message: missedEventToToast(e),
        expiresAt: Date.now() + TOAST_TTL_DEFAULT_MS,
      }))
      set((state) => ({ toasts: state.toasts.concat(ts) }))
      return
    }
    if (msg.type === 'waiting') {
      set({ status: 'connected', feed_status: 'delayed_60s' })
      return
    }
    if (msg.type === 'replaced') {
      set({ status: 'replaced' })
      return
    }
    if (msg.type === 'price_history') {
      set({ priceRing: msg.points })
      return
    }
  },

  beginOptimisticMove: (target) => {
    set((state) => {
      if (!state.self) return state
      return {
        self: { ...state.self, optimistic_busy: true, optimistic_target: target },
      }
    })
  },

  clearOptimisticMove: () => {
    set((state) => {
      if (!state.self) return state
      return {
        self: { ...state.self, optimistic_busy: false, optimistic_target: null },
      }
    })
  },

  setTimeframe: (tf) => set({ timeframe: tf }),

  pushToast: (message, ttlMs = TOAST_TTL_DEFAULT_MS) => {
    set((state) => ({
      toasts: state.toasts.concat({
        id: ++toastSeq,
        message,
        expiresAt: Date.now() + ttlMs,
      }),
    }))
  },

  dismissToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },

  reset: () => {
    set({
      status: 'idle',
      tick_id: 0,
      price: null,
      feed_status: 'delayed_60s',
      self: null,
      players: new Map(),
      tombstones: [],
      priceRing: [],
      toasts: [],
      selfLiquidatedAt: null,
    })
  },
}))

// Re-export the type for convenience
export type StoreApi = ReturnType<typeof useStore.getState>
